import type { AppItem } from "@/types/app";
import type { RuntimeConfig, TenantKind } from "@/types/config";

/**
 * The SPA is compiled once and deployed to many environments. All
 * environment-specific values are therefore loaded at runtime from
 * `GET /config.json` – served by the Cloudflare Worker (worker/index.ts)
 * using values from `wrangler.jsonc → vars` or the dashboard's
 * *Variables and Secrets* pane.
 *
 * During `npm run dev` or `npm run build` the fetch will 404 (no Worker
 * in front of Vite), so we fall back to Vite's `import.meta.env` which
 * is populated from `.env.local`.
 */

let cached: RuntimeConfig | null = null;
let promise: Promise<RuntimeConfig> | null = null;

const DEFAULT_SCOPES = ["openid", "profile", "email", "offline_access"];

function splitScopes(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
}

function parseTenantKind(raw: string | undefined): TenantKind {
  const v = (raw ?? "").trim().toLowerCase();
  if (v === "ciam" || v === "workforce") return v;
  return "auto";
}

function parseAppsJson(raw: string | undefined): AppItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (a): a is AppItem =>
        !!a &&
        typeof a === "object" &&
        typeof (a as AppItem).id === "string" &&
        typeof (a as AppItem).name === "string",
    );
  } catch (err) {
    console.warn("[runtimeConfig] VITE_APPS_JSON is not valid JSON", err);
    return [];
  }
}

function buildFromRaw(raw: Record<string, string>): RuntimeConfig {
  const pick = (key: string): string =>
    raw[key] ?? (import.meta.env as Record<string, string | undefined>)[key] ?? "";

  return {
    clientId: pick("VITE_AAD_CLIENT_ID"),
    authority: pick("VITE_AAD_AUTHORITY"),
    knownAuthority: pick("VITE_AAD_KNOWN_AUTHORITY"),
    redirectUri:
      pick("VITE_AAD_REDIRECT_URI") ||
      `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri:
      pick("VITE_AAD_POST_LOGOUT_REDIRECT_URI") || window.location.origin + "/",
    scopes: splitScopes(pick("VITE_AAD_SCOPES")).length
      ? splitScopes(pick("VITE_AAD_SCOPES"))
      : DEFAULT_SCOPES,
    graphScopes: splitScopes(pick("VITE_GRAPH_SCOPES")),
    tenantKind: parseTenantKind(pick("VITE_AAD_TENANT_KIND")),
    companyName: pick("VITE_COMPANY_NAME") || "Your organisation",
    portalTitle: pick("VITE_PORTAL_TITLE") || "My Apps",
    passwordResetUrl: pick("VITE_PASSWORD_RESET_URL"),
    expectedEmailDomain: pick("VITE_EXPECTED_EMAIL_DOMAIN").trim().toLowerCase(),
    apps: parseAppsJson(pick("VITE_APPS_JSON")),
  };
}

/**
 * Load the runtime configuration exactly once. Subsequent callers
 * receive the cached result.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  if (promise) return promise;

  promise = (async () => {
    let remote: Record<string, string> = {};
    try {
      const res = await fetch("/config.json", {
        cache: "no-store",
        credentials: "omit",
      });
      if (res.ok) {
        const json = (await res.json()) as Record<string, unknown>;
        for (const [k, v] of Object.entries(json)) {
          if (typeof v === "string") remote[k] = v;
        }
      }
    } catch {
      remote = {};
    }

    const cfg = buildFromRaw(remote);
    cached = cfg;
    return cfg;
  })();

  return promise;
}

/**
 * Synchronous accessor – throws when called before the config has been
 * loaded. Meant for components that render AFTER the AuthProvider has
 * awaited `loadRuntimeConfig`.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error(
      "[runtimeConfig] getRuntimeConfig() called before loadRuntimeConfig() resolved",
    );
  }
  return cached;
}

/** Testing / Storybook helper. Not used by production code paths. */
export function __setRuntimeConfigForTest(cfg: RuntimeConfig): void {
  cached = cfg;
}
